import type { ReactNode } from 'react'
import styles from './Markdown.module.css'

/**
 * Markdown, rendered as React elements.
 *
 * **Why this is hand-written.** The assistant answers in markdown — its own system prompt is
 * written in it, so the model mirrors it back — and before this the SPA showed the raw
 * `**asterisks**` and `##` that the Blazor advisor window had always rendered. The obvious fix
 * is `react-markdown`, and the reason it is not used is proportion: it brings roughly forty
 * transitive packages for one text surface in an app that runs on five runtime dependencies,
 * and this file covers the subset a chat actually produces in about two hundred lines that are
 * under test.
 *
 * **It is safe by construction, which is the part that matters for model output.** Nothing
 * here ever builds an HTML string or touches `dangerouslySetInnerHTML`; every branch returns
 * React elements, so text can only ever become text. Link targets are additionally restricted
 * to `http`, `https`, `mailto` and same-origin paths — a `javascript:` href is rendered as the
 * literal text it was written as.
 *
 * **What it deliberately does not do.** Reference links, footnotes, setext headings, HTML
 * passthrough, and nested emphasis edge cases from the CommonMark suite. Anything unsupported
 * degrades to the literal characters, which is a readable failure rather than a broken one —
 * and is why a partial implementation is defensible here where it would not be in a document
 * editor.
 *
 * Streaming is a first-class case: the text passed in is usually half an answer, so an
 * unterminated code fence renders as a code block rather than swallowing the rest.
 */
export function Markdown({ text }: { text: string }) {
  const blocks = parseBlocks(text.split('\n'))
  return <div className={styles.prose}>{blocks.map(renderBlock)}</div>
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'code'; lines: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'list'; ordered: boolean; start: number; items: string[][] }
  | { kind: 'table'; header: string[]; rows: string[][] }
  | { kind: 'rule' }

const FENCE = /^\s*(```|~~~)/
const HEADING = /^ {0,3}(#{1,6})\s+(.*?)\s*#*\s*$/
const RULE = /^ {0,3}([-*_])(\s*\1){2,}\s*$/
const QUOTE = /^ {0,3}> ?(.*)$/
const ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/
const TABLE_DIVIDER = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/

function parseBlocks(lines: string[]): Block[] {
  const blocks: Block[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index] ?? ''

    if (line.trim() === '') {
      index += 1
      continue
    }

    if (FENCE.test(line)) {
      const marker = (FENCE.exec(line)?.[1] ?? '```') as string
      const body: string[] = []
      index += 1
      // An unclosed fence runs to the end, which is what a half-streamed answer looks like.
      while (index < lines.length && !(lines[index] ?? '').trimStart().startsWith(marker)) {
        body.push(lines[index] ?? '')
        index += 1
      }
      index += 1 // the closing fence, or past the end
      blocks.push({ kind: 'code', lines: body })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading !== null) {
      blocks.push({ kind: 'heading', level: (heading[1] ?? '#').length, text: heading[2] ?? '' })
      index += 1
      continue
    }

    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' })
      index += 1
      continue
    }

    if (QUOTE.test(line)) {
      const body: string[] = []
      while (index < lines.length && QUOTE.test(lines[index] ?? '')) {
        body.push(QUOTE.exec(lines[index] ?? '')?.[1] ?? '')
        index += 1
      }
      blocks.push({ kind: 'quote', lines: body })
      continue
    }

    // A table needs its divider on the next line; without one these are ordinary paragraphs.
    if (line.includes('|') && TABLE_DIVIDER.test(lines[index + 1] ?? '')) {
      const header = splitRow(line)
      const rows: string[][] = []
      index += 2
      while (index < lines.length && (lines[index] ?? '').includes('|')) {
        rows.push(splitRow(lines[index] ?? ''))
        index += 1
      }
      blocks.push({ kind: 'table', header, rows })
      continue
    }

    if (ITEM.test(line)) {
      const [list, next] = parseList(lines, index)
      blocks.push(list)
      index = next
      continue
    }

    const paragraph: string[] = []
    while (index < lines.length) {
      const current = lines[index] ?? ''
      if (current.trim() === '') break
      if (FENCE.test(current) || HEADING.test(current) || RULE.test(current)) break
      if (QUOTE.test(current) || ITEM.test(current)) break
      if (current.includes('|') && TABLE_DIVIDER.test(lines[index + 1] ?? '')) break
      paragraph.push(current.trim())
      index += 1
    }
    blocks.push({ kind: 'paragraph', lines: paragraph })
  }

  return blocks
}

/**
 * One list, from its first item to the first line that is not part of it.
 *
 * Items collect their continuation lines rather than only their first, so a nested list or a
 * second paragraph inside an item survives — those lines are dedented and parsed as blocks in
 * their own right, which is what makes nesting work at any depth without special-casing it.
 */
function parseList(lines: string[], from: number): [Block, number] {
  const first = ITEM.exec(lines[from] ?? '')
  const baseIndent = (first?.[1] ?? '').length
  const marker = first?.[2] ?? '-'
  const ordered = /\d/.test(marker)
  const start = ordered ? Number.parseInt(marker, 10) : 1

  const items: string[][] = []
  let index = from
  let blanks = 0

  while (index < lines.length) {
    const line = lines[index] ?? ''

    if (line.trim() === '') {
      // One blank line inside a list is a loose list; two end it.
      blanks += 1
      if (blanks > 1) break
      items[items.length - 1]?.push('')
      index += 1
      continue
    }

    const item = ITEM.exec(line)
    const indent = line.length - line.trimStart().length

    if (item !== null && (item[1] ?? '').length <= baseIndent) {
      // A different marker family at the same level starts a different list.
      if (/\d/.test(item[2] ?? '') !== ordered) break
      items.push([item[3] ?? ''])
      blanks = 0
      index += 1
      continue
    }

    if (indent > baseIndent) {
      items[items.length - 1]?.push(line.slice(Math.min(indent, baseIndent + 2)))
      blanks = 0
      index += 1
      continue
    }

    if (blanks > 0) break

    // A lazy continuation: an unindented wrapped line belonging to the item above.
    items[items.length - 1]?.push(line.trim())
    index += 1
  }

  return [{ kind: 'list', ordered, start, items }, index]
}

function splitRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
}

function renderBlock(block: Block, key: number): ReactNode {
  switch (block.kind) {
    case 'heading': {
      // Headings start at h3: the page owns h1 and the assistant panel owns h2, so a message
      // that opened at h1 would break the document outline of whatever screen it is over.
      const Tag = `h${Math.min(block.level + 2, 6)}` as 'h3' | 'h4' | 'h5' | 'h6'
      return (
        <Tag key={key} className={styles.heading}>
          {inline(block.text)}
        </Tag>
      )
    }

    case 'code':
      return (
        <pre key={key} className={styles.pre}>
          <code>{block.lines.join('\n')}</code>
        </pre>
      )

    case 'quote':
      return (
        <blockquote key={key} className={styles.quote}>
          {parseBlocks(block.lines).map(renderBlock)}
        </blockquote>
      )

    case 'rule':
      return <hr key={key} className={styles.rule} />

    case 'list': {
      const children = block.items.map((item, itemKey) => (
        <li key={itemKey} className={styles.item}>
          {renderItem(item)}
        </li>
      ))

      return block.ordered ? (
        <ol key={key} className={styles.list} start={block.start}>
          {children}
        </ol>
      ) : (
        <ul key={key} className={styles.list}>
          {children}
        </ul>
      )
    }

    case 'table':
      return (
        // Its own scroll container: a wide table must not make the page scroll sideways.
        <div key={key} className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                {block.header.map((cell, cellKey) => (
                  <th key={cellKey}>{inline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowKey) => (
                <tr key={rowKey}>
                  {row.map((cell, cellKey) => (
                    <td key={cellKey}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )

    case 'paragraph':
      return (
        <p key={key} className={styles.paragraph}>
          {block.lines.map((line, lineKey) => (
            // Soft line breaks are kept. CommonMark would join these into one run, but this is
            // chat: somebody who pressed Enter meant it, and so did the model.
            <span key={lineKey}>
              {lineKey > 0 && <br />}
              {inline(line)}
            </span>
          ))}
        </p>
      )
  }
}

/** A list item's content, unwrapped when it is the single paragraph most items are. */
function renderItem(lines: string[]): ReactNode {
  const blocks = parseBlocks(lines)
  const only = blocks[0]

  if (blocks.length === 1 && only?.kind === 'paragraph') {
    return only.lines.map((line, key) => (
      <span key={key}>
        {key > 0 && <br />}
        {inline(line)}
      </span>
    ))
  }

  return blocks.map(renderBlock)
}

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------

const INLINE = new RegExp(
  [
    '(`+)([\\s\\S]*?[^`])\\1(?!`)', // code span
    '\\*\\*([\\s\\S]+?)\\*\\*', // bold
    '__([\\s\\S]+?)__', // bold
    '~~([\\s\\S]+?)~~', // strikethrough
    '\\*([^*\\s][\\s\\S]*?)\\*', // italic
    '(?<![A-Za-z0-9_])_([^_\\s][\\s\\S]*?)_(?![A-Za-z0-9_])', // italic
    // The href allows one level of balanced parentheses, because real URLs contain them —
    // `…/wiki/Fate_(role-playing_game)` is the kind of link Bob cites. Stopping at the first
    // `)` would truncate the target and strand the remainder as loose text.
    '\\[([^\\]]*)\\]\\(((?:[^()\\s]|\\([^()\\s]*\\))+)(?:\\s+"[^"]*")?\\)', // link
  ].join('|'),
  'g',
)

/**
 * Emphasis, code spans and links within one line.
 *
 * Code spans win, and they win by being first in the alternation: `**` inside backticks is two
 * asterisks, which is the whole point of a code span. Everything else recurses, so bold inside
 * a link and italics inside bold both come out right.
 *
 * **The scanner is a fresh instance per call, and that is load-bearing.** A `g`-flagged regex
 * carries `lastIndex` on the object, so a shared one would have every recursive call rewind its
 * caller's scan position — which is not a subtle mis-render but an unbounded loop: the outer
 * call re-matches what it just consumed, recurses again, and allocates until the heap is gone.
 */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = []
  const scanner = new RegExp(INLINE.source, INLINE.flags)
  let last = 0
  let key = 0

  let match = scanner.exec(text)

  while (match !== null) {
    if (match.index > last) out.push(text.slice(last, match.index))

    const [, , code, boldStar, boldUnderscore, struck, italicStar, italicUnderscore, label, href] =
      match

    if (code !== undefined) {
      out.push(<code key={key}>{code}</code>)
    } else if (boldStar !== undefined || boldUnderscore !== undefined) {
      out.push(<strong key={key}>{inline(boldStar ?? boldUnderscore ?? '')}</strong>)
    } else if (struck !== undefined) {
      out.push(<del key={key}>{inline(struck)}</del>)
    } else if (italicStar !== undefined || italicUnderscore !== undefined) {
      out.push(<em key={key}>{inline(italicStar ?? italicUnderscore ?? '')}</em>)
    } else if (label !== undefined) {
      const safe = safeHref(href ?? '')
      out.push(
        safe === null ? (
          // A target this app will not follow is shown as what it literally said, rather than
          // as a link that does nothing or, worse, one that does something.
          <span key={key}>{match[0]}</span>
        ) : (
          <a key={key} href={safe} target="_blank" rel="noreferrer noopener">
            {inline(label)}
          </a>
        ),
      )
    }

    key += 1
    last = match.index + match[0].length
    match = scanner.exec(text)
  }

  if (last < text.length) out.push(text.slice(last))
  return out
}

/**
 * The link schemes this app will render as links.
 *
 * An allow-list rather than a `javascript:` deny-list: the text being linkified came from a
 * language model, and enumerating what is safe is the only version of this check that stays
 * correct as new schemes appear.
 */
function safeHref(href: string): string | null {
  const trimmed = href.trim()
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed
  return /^(https?:|mailto:)/i.test(trimmed) ? trimmed : null
}
